<template>
  <div class="radarchart">
    <h2 v-text="snippets.RadarChart.title"></h2>
    <div class="radarchart-filters">
      <b-field>
        <p class="control">
          <b-select
            aria-role="list"
            v-model="filterTeam1"
            :placeholder="snippets.RadarChart.filters.placeholderFilterTeam1"
          >
            <option v-for="item in filterList" :value="item" :key="item.id">
              {{ item.label }}
            </option>
          </b-select>
        </p>
        <p class="control">
          <b-button
            label="X"
            :disabled="!filterTeam1"
            :title="snippets.clearButtonTitle"
            @click="filterTeam1 = null"
          />
        </p>
      </b-field>

      <b-field>
        <p class="control">
          <b-select
            aria-role="list"
            v-model="filterTeam2"
            :placeholder="snippets.RadarChart.filters.placeholderFilterTeam2"
          >
            <option v-for="item in filterList" :value="item" :key="item.id">
              {{ item.label }}
            </option>
          </b-select>
        </p>
        <p class="control">
          <b-button
            label="X"
            :disabled="!filterTeam2"
            :title="snippets.clearButtonTitle"
            @click="filterTeam2 = null"
          />
        </p>
      </b-field>

      <div class="radarchart-filters-switches">
        <b-switch v-model="contestsExtended" type="is-info">
          <span v-text="snippets.RadarChart.filters.contestsExtended"></span>
        </b-switch>
        <b-switch v-model="showAbsoluteValues" type="is-info">
          <span v-text="snippets.RadarChart.filters.showAbsoluteValues"></span>
        </b-switch>
      </div>
    </div>
    <Radar
      :chart-options="chartOptions"
      :chart-data="chartData"
      chart-id="radarchart-chart-canvas"
      :dataset-id-key="datasetIdKey"
      :css-classes="cssClasses"
      :width="width"
      :height="height"
      class="radarchart-chart"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { Radar } from "vue-chartjs/legacy";
import {
  Chart as ChartJS,
  Filler,
  Title,
  Tooltip,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
} from "chart.js";

ChartJS.register(
  Filler,
  Title,
  Tooltip,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement
);

export default {
  name: "RadarChart",
  components: {
    Radar,
  },
  props: {
    datasetIdKey: {
      type: String,
      default: "label",
    },
    width: {
      type: Number,
      default: 800,
    },
    height: {
      type: Number,
      default: 400,
    },
    cssClasses: {
      default: "",
      type: String,
    },
  },
  data() {
    return {
      contestMap: null,
      contestMapExtended: null,
      contestsExtended: false,
      filterTeam1: null,
      filterTeam2: null,
      filterList: [],
      showAbsoluteValues: false,
      contestData: [],
      teamData: [],
      chartData: {
        labels: [],
        datasets: [],
      },
      chartOptions: {
        responsive: true,
        plugins: {
          tooltip: {
            callbacks: {
              label: (context) => {
                let label = context.dataset.label || "";
                if (label) {
                  label += ": ";
                }
                if (this.showAbsoluteValues) {
                  label += context.formattedValue;
                } else {
                  if (context.parsed.r !== null) {
                    // label += (0.0 + context.parsed.r).toFixed(2) + " %";
                    label += Math.floor(0.0 + context.parsed.r) + " %";
                  }
                }
                return label;
              },
            },
          },
        },
        scales: {
          r: {
            max: 100,
            suggestedMin: 0,
            ticks: {
              // eslint-disable-next-line no-unused-vars
              callback: (value, index, ticks) =>
                this.showAbsoluteValues ? value : value + " %",
            },
          },
        },
        options: {
          elements: {
            line: {
              borderWidth: 3,
            },
          },
        },
      },
      bgColorForChart: ["rgba(0, 0, 255, 0.25)", "rgba(255, 0, 0, 0.25)"],
    };
  },
  computed: {
    ...mapState(["snippets"]),
  },
  created() {
    this.contestData = this.$store.getters.contests;
    this.teamData = this.$store.getters.teams;
    this.filterList = this.teamData.map((team) => {
      return { id: team.team, label: team.team_name };
    });
    let contestMapExtended = [];
    let contestData = this.contestData.filter(
      (contest) => contest.cont_subcont !== "11_0"
    );
    this.contestMap = contestData.map((contest) => {
      contestMapExtended = contestMapExtended.concat(
        (contest.sub_contests || []).map((subcontest) => {
          return {
            contestNr: subcontest.cont_subcont,
            name: subcontest.sub_contest_name,
            maxPoints: subcontest.max_points,
          };
        })
      );
      return {
        contestNr: contest.cont_subcont,
        name: contest.contest_name,
        maxPoints: contest.max_points,
      };
    });
    this.contestMapExtended = contestMapExtended;
    this.updateChart();
  },
  watch: {
    contestsExtended() {
      this.updateChart();
    },
    filterTeam1() {
      this.updateChart();
    },
    filterTeam2() {
      this.updateChart();
    },
    showAbsoluteValues(newValue) {
      this.chartOptions.scales.r.max = newValue ? undefined : 100;
      this.updateChart();
    },
  },
  methods: {
    updateChart() {
      let contestMap = this.contestsExtended
        ? this.contestMapExtended
        : this.contestMap;
      this.chartData.labels = contestMap.map((entry) => entry.name);
      let teamsFiltered = this.teamData.filter((team) => {
        if (this.filterTeam1 && team.team === this.filterTeam1.id) {
          return true;
        }
        if (this.filterTeam2 && team.team === this.filterTeam2.id) {
          return true;
        }
        return false;
      });
      let datasets = teamsFiltered.map((team, index) => {
        let chartData = contestMap.map((entry) => {
          // { contestNr, name, }
          let value = 0;
          let tmp = (team.scores || []).find(
            (score) => team.team + "_" + entry.contestNr === score.marker
          );
          if (tmp) {
            value =
              (tmp?.bonus_points ?? 0) +
              (tmp?.contest_points ?? 0) +
              (tmp?.penalty_points ?? 0);
          }
          if (!this.showAbsoluteValues) {
            const maxPoints = entry?.maxPoints || value;
            // value = ((value / maxPoints) * 100).toFixed(2);
            value = (value / maxPoints) * 100;
          }
          value = Math.floor(value);
          return value;
        });
        return {
          label: team.team_name,
          data: chartData,
          backgroundColor: this.bgColorForChart[index],
          borderColor: this.bgColorForChart[index],
          fill: true,
        };
      });
      if (datasets.length === 0) {
        datasets = [
          {
            label: this.snippets.RadarChart.fallbackDatasetLabel,
            data: [],
          },
        ];
      }
      this.chartData.datasets = datasets;
    },
  },
};
</script>
<style>
.radarchart {
  margin: 3rem auto auto;
  max-width: 1000px;
  max-height: 1000px;
}
.radarchart .radarchart-filters {
  margin: 0.5rem 0;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}
.radarchart .radarchart-filters-switches {
  margin-top: 0.5rem;
}
.radarchart .radarchart-filters > * {
  margin-left: 0.5rem;
}
</style>
